import React from "react";
import { Box, Typography, Stack, Divider } from "@mui/material";
import {
  calculateUnitCost,
  componentCostPerGram,
} from "../../../utils/costUtils";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

const n = (v, f = 0) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : f;
};
const round2 = (v) => Math.round(n(v) * 100) / 100;

function fmtPerUnit(ppu, unit) {
  const v = n(ppu);
  const txt = v < 0.01 ? v.toFixed(4) : v.toFixed(2);
  return `$${txt} per ${unit}`;
}
function maybePer100(ppu, unit) {
  const u = (unit || "").toLowerCase();
  if ((u === "g" || u === "ml") && ppu > 0 && ppu < 0.02) {
    return ` · $${(ppu * 100).toFixed(2)} per 100 ${u}`;
  }
  return "";
}

export default function CostProfileRowDetails({ profile, open }) {
  if (!open) return null;

  // ---- Exact math (matches table cell / calculateUnitCost) ----
  const unitCostExact = calculateUnitCost(profile);

  // ---- Build ingredient lines (with exact + rounded) ----
  const ingLines = (profile.ingredients || []).map((ing) => {
    const qty = n(ing.quantity);
    const unit = ing.unit || "";
    const ppu = n(ing.costPerUnitSnapshot);
    const exact = qty * ppu; // exact line cost
    const rounded = round2(exact);
    return {
      name: ing.ingredientId?.name || ing.ingredientId,
      qty,
      unit,
      ppu,
      exact,
      rounded,
      syncedAt: ing.lastSyncedAt,
      err: ing.syncError,
    };
  });

  // ---- Build component lines (requires components.componentId populated) ----
  const compLines = (profile.components || []).map((c) => {
    const compDoc =
      c &&
      c.componentId &&
      typeof c.componentId === "object" &&
      (c.componentId.ingredients ||
        c.componentId.isComponent ||
        c.componentId.componentYield)
        ? c.componentId
        : null;

    const name =
      compDoc?.name ||
      compDoc?.productId?.displayName ||
      (typeof c.componentId === "string"
        ? c.componentId.slice(-6)
        : "Component");

    const used = n(c.usedGrams);
    const ppg = compDoc ? componentCostPerGram(compDoc) : 0; // $/g
    const exact = used * ppg;
    const rounded = round2(exact);

    return { name, used, ppg, exact, rounded };
  });

  // ---- Packaging / Labor / Adjustments (exact + rounded) ----
  const pkgLines = profile.includePackaging
    ? (profile.packaging || []).map((p) => {
        const exact = n(p.unitCost);
        return { name: p.name || "Packaging", exact, rounded: round2(exact) };
      })
    : [];

  const laborExact = profile.includeLabor
    ? (n(profile.laborMinutes) / 60) * n(profile.laborRate)
    : 0;
  const laborRounded = round2(laborExact);

  const adjLines = (profile.manualAdjustments || []).map((a) => {
    const exact = n(a.amount);
    return { name: a.label || "Adjustment", exact, rounded: round2(exact) };
  });

  // ---- Subtotals before batch/waste (exact vs sum of rounded) ----
  const sumExactBefore = [
    ...ingLines.map((x) => x.exact),
    ...compLines.map((x) => x.exact),
    ...pkgLines.map((x) => x.exact),
    laborExact,
    ...adjLines.map((x) => x.exact),
  ].reduce((s, v) => s + v, 0);

  const sumRoundedBefore = [
    ...ingLines.map((x) => x.rounded),
    ...compLines.map((x) => x.rounded),
    ...pkgLines.map((x) => x.rounded),
    laborRounded,
    ...adjLines.map((x) => x.rounded),
  ].reduce((s, v) => s + v, 0);

  const batchSize = Math.max(1, n(profile.batchSize, 1));
  const wastePct = n(profile.wasteBufferPercent);
  const unitRoundedFromLines =
    (sumRoundedBefore / batchSize) * (1 + wastePct / 100);

  const roundingDiff = round2(unitCostExact - unitRoundedFromLines); // may be ±$0.01

  const margin =
    profile.unitPrice && !profile.isComponent
      ? ((n(profile.unitPrice) - unitCostExact) / n(profile.unitPrice)) * 100
      : 0;

  return (
    <Box sx={{ mt: 2, px: 1 }}>
      {/* Ingredients */}
      <Typography variant="subtitle1" fontWeight={600}>
        Ingredient Breakdown
      </Typography>

      <Stack spacing={1} mt={1}>
        {ingLines.map((row, i) => (
          <Box key={i}>
            <Typography variant="body2" fontWeight={500}>
              {row.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {row.qty} {row.unit} × {fmtPerUnit(row.ppu, row.unit)} = $
              {row.rounded.toFixed(2)}
              {maybePer100(row.ppu, row.unit)}
            </Typography>
            {row.syncedAt && (
              <Typography variant="caption" color="gray">
                Synced {dayjs(row.syncedAt).fromNow()}
              </Typography>
            )}
            {row.err && (
              <Typography variant="caption" color="error">
                {row.err}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>

      {/* Reusable Components */}
      {compLines.length > 0 && (
        <Box mt={3}>
          <Typography variant="subtitle1" fontWeight={600}>
            Reusable Components
          </Typography>
          <Stack spacing={0.5} mt={1}>
            {compLines.map((c, i) => (
              <Typography key={i} variant="body2">
                {c.name}: {c.used} g × ${c.ppg.toFixed(4)} per g = $
                {c.rounded.toFixed(2)}
                {c.ppg > 0 && c.ppg < 0.02
                  ? ` · $${(c.ppg * 100).toFixed(2)} per 100 g`
                  : ""}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}

      {/* Packaging */}
      {pkgLines.length > 0 && (
        <Box mt={3}>
          <Typography variant="subtitle1" fontWeight={600}>
            Packaging
          </Typography>
          <Stack spacing={0.5} mt={1}>
            {pkgLines.map((p, i) => (
              <Typography key={i} variant="body2">
                {p.name}: ${p.rounded.toFixed(2)}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}

      {/* Labor */}
      {profile.includeLabor && (
        <Box mt={3}>
          <Typography variant="subtitle1" fontWeight={600}>
            Labor
          </Typography>
          <Typography variant="body2">
            {n(profile.laborMinutes)} minutes @ ${n(profile.laborRate)}/hr = $
            {laborRounded.toFixed(2)}
          </Typography>
        </Box>
      )}

      {/* Manual Adjustments */}
      {adjLines.length > 0 && (
        <Box mt={3}>
          <Typography variant="subtitle1" fontWeight={600}>
            Other Costs
          </Typography>
          <Stack spacing={0.5} mt={1}>
            {adjLines.map((a, i) => (
              <Typography key={i} variant="body2">
                {a.name}: ${a.rounded.toFixed(2)}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}

      {/* Summary */}
      <Divider sx={{ my: 2 }} />
      <Typography variant="body2">
        <strong>Waste Buffer:</strong> {wastePct}%
      </Typography>
      <Typography variant="body2">
        <strong>Unit Cost:</strong> ${unitCostExact.toFixed(2)}
        {roundingDiff !== 0 && (
          <span style={{ color: "#6b7280" }}>
            {" "}
            · Rounding difference: {roundingDiff >= 0 ? "+" : "-"}$
            {Math.abs(roundingDiff).toFixed(2)}
          </span>
        )}
      </Typography>
      {!profile.isComponent && (
        <>
          <Typography variant="body2">
            <strong>Listed Price:</strong> ${n(profile.unitPrice).toFixed(2)}
          </Typography>
          <Typography variant="body2">
            <strong>Gross Margin:</strong> {margin.toFixed(1)}%
          </Typography>
        </>
      )}
    </Box>
  );
}
